// src/pages/NotificationsPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import api from '../utils/api';
import '../styles/notifications-page.css';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  useEffect(() => {
    fetchNotifications();
  }, []);
  
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      
      const response = await api.get('/api/notifications');
      setNotifications(response.data.notifications);
      
      // Mark all as read in the background
      api.put('/api/notifications/read-all');
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };
  
  const renderNotificationContent = (notification) => {
    const { type, fromUser, text } = notification;
    
    switch (type) {
      case 'follow':
        return (
          <div className="notification-content">
            <span className="notification-icon follow">👥</span>
            <div className="notification-text">
              <Link to={`/profile/${fromUser}`} className="notification-user">
                {fromUser}
              </Link> started following you
            </div>
          </div>
        );
        
      case 'like':
      case 'reaction':
        return (
          <div className="notification-content">
            <span className="notification-icon like">❤️</span>
            <div className="notification-text">
              <Link to={`/profile/${fromUser}`} className="notification-user">
                {fromUser}
              </Link> liked your {notification.subtype === 'comment' ? 'comment' : 'post'}
              {notification.postId && (
                <Link to={`/post/${notification.postId}`} className="notification-link">
                  View post
                </Link>
              )}
            </div>
          </div>
        );
        
      case 'comment':
        return (
          <div className="notification-content">
            <span className="notification-icon comment">💬</span>
            <div className="notification-text">
              <Link to={`/profile/${fromUser}`} className="notification-user">
                {fromUser}
              </Link> commented on your post
              {notification.postId && (
                <Link to={`/post/${notification.postId}`} className="notification-link">
                  View comment
                </Link>
              )}
            </div>
          </div>
        );
        
      case 'mention':
        return (
          <div className="notification-content">
            <span className="notification-icon mention">@</span>
            <div className="notification-text">
              <Link to={`/profile/${fromUser}`} className="notification-user">
                {fromUser}
              </Link> mentioned you in a {notification.commentId ? 'comment' : 'post'}
              {notification.postId && (
                <Link to={`/post/${notification.postId}`} className="notification-link">
                  View {notification.commentId ? 'comment' : 'post'}
                </Link>
              )}
            </div>
          </div>
        );
        
      case 'share':
        return (
          <div className="notification-content">
            <span className="notification-icon share">🔄</span>
            <div className="notification-text">
              <Link to={`/profile/${fromUser}`} className="notification-user">
                {fromUser}
              </Link> shared your post
              {notification.sharedPostId && (
                <Link to={`/post/${notification.sharedPostId}`} className="notification-link">
                  View share
                </Link>
              )}
            </div>
          </div>
        );
        
      default:
        return (
          <div className="notification-content">
            <span className="notification-icon default">📣</span>
            <div className="notification-text">{text}</div>
          </div>
        );
    }
  };
  
  if (loading) {
    return <div className="loading">Loading notifications...</div>;
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  return (
    <div className="notifications-page">
      <h1 className="page-title">Notifications</h1>
      
      {notifications.length > 0 ? (
        <div className="notifications-list">
          {notifications.map(notification => {
            const time = formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true });
            
            return (
              <div 
                key={notification.id} 
                className={`notification-item ${!notification.read ? 'unread' : ''}`}
              >
                {renderNotificationContent(notification)}
                <div className="notification-time">{time}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <h3>No notifications yet</h3>
          <p>When someone interacts with you, you'll see it here.</p>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;